import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantFormat } from '../../hooks/useTenantFormat';
import { useToast } from '../../hooks/useToast';
import { api } from '../../lib/api';
import { GlassCard } from '../../components/GlassCard';
import { ArrowDownLeft, ArrowUpRight, ArrowLeftRight, ArrowLeft, RotateCcw, FileText } from 'lucide-react';

const TYPE_COLORS = {
  payment: 'bg-red-50 text-red-600 border-red-200',
  receipt: 'bg-emerald-50 text-emerald-600 border-emerald-200',
  contra: 'bg-blue-50 text-blue-600 border-blue-200',
};
const TYPE_ICONS = { payment: ArrowDownLeft, receipt: ArrowUpRight, contra: ArrowLeftRight };

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function VoucherDetail() {
  const { id } = useParams();
  const { token } = useAuth();
  const toast = useToast();
  const { formatMoney } = useTenantFormat();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reversing, setReversing] = useState(false);

  useEffect(() => {
    if (!token || !id) return;
    setLoading(true);
    api.voucherGet(token, id)
      .then(r => setData(r))
      .catch(e => { if (e.status !== 401) toast.show(e.message || 'Failed to load', 'error'); })
      .finally(() => setLoading(false));
  }, [token, id, toast]);

  const v = data?.voucher || data;
  const meta = v?.voucher_meta || {};
  const vt = v?.voucher_type || 'payment';
  const Icon = TYPE_ICONS[vt] || FileText;
  const partyBalance = data?.party_balance;

  const canReverse = v && !v.reversed_at && !v.reversal_of;
  const isOld = v?.created_at && (Date.now() - new Date(v.created_at).getTime()) > 24 * 60 * 60 * 1000;

  const handleReverse = async () => {
    if (!canReverse) return;
    if (!window.confirm('Are you sure you want to reverse this voucher? This will create a counter-entry.')) return;
    setReversing(true);
    try {
      await api.erpJournalDelete(token, v.entry_id);
      toast.show('Voucher reversed', 'success');
      // Reload
      const r = await api.voucherGet(token, id);
      setData(r);
    } catch (err) {
      toast.show(err?.message || 'Reverse failed', 'error');
    } finally { setReversing(false); }
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="voucher-detail-page">
        <div className="py-20 flex justify-center"><div className="w-6 h-6 border-2 border-brand-purple border-t-transparent rounded-full animate-spin" /></div>
      </div>
    );
  }

  if (!v) {
    return (
      <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="voucher-detail-page">
        <GlassCard className="p-12 text-center">
          <p className="text-gray-500">Voucher not found.</p>
          <Link to="/vouchers" className="text-brand-purple text-sm font-bold hover:underline mt-2 inline-block">Back to vouchers</Link>
        </GlassCard>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="voucher-detail-page">
      {/* Back link */}
      <Link to="/vouchers" className="inline-flex items-center gap-1.5 text-gray-500 text-sm font-semibold hover:text-gray-700 mb-4" data-testid="voucher-back">
        <ArrowLeft size={14} /> Back to vouchers
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${vt === 'payment' ? 'bg-red-50 border border-red-200' : vt === 'receipt' ? 'bg-emerald-50 border border-emerald-200' : 'bg-blue-50 border border-blue-200'}`}>
            <Icon size={24} className={vt === 'payment' ? 'text-red-500' : vt === 'receipt' ? 'text-emerald-600' : 'text-blue-600'} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-gray-900 font-extrabold text-xl capitalize">{vt} Voucher</h1>
              <span className={`inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase border ${TYPE_COLORS[vt]}`}>{vt}</span>
              {v.reversed_at && <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase bg-gray-100 text-gray-500 border border-gray-200">Reversed</span>}
              {v.reversal_of && <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase bg-amber-50 text-amber-600 border border-amber-200">Reversal</span>}
            </div>
            <p className="text-gray-500 text-sm mt-0.5">{fmtDate(v.entry_date)}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-gray-900 font-extrabold text-2xl tabular-nums" data-testid="voucher-amount">{formatMoney(meta.amount || v.debit_total)}</p>
          {meta.mode && <p className="text-gray-500 text-xs uppercase font-bold mt-1">{meta.mode}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Main info */}
        <div className="lg:col-span-2 space-y-4">
          {/* Summary */}
          <GlassCard className="p-5">
            <h3 className="text-gray-500 text-xs font-bold uppercase tracking-wider mb-3">Details</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              {meta.party_name && (
                <div>
                  <p className="text-gray-400 text-xs font-bold uppercase">Party</p>
                  <p className="text-gray-900 font-semibold mt-0.5">{meta.party_name}</p>
                  {meta.party_type && <p className="text-gray-400 text-[10px] uppercase">{meta.party_type}</p>}
                </div>
              )}
              {meta.from_account && (
                <div>
                  <p className="text-gray-400 text-xs font-bold uppercase">From Account</p>
                  <p className="text-gray-900 font-semibold mt-0.5">{meta.from_account === 'cash' ? 'Cash' : meta.from_account}</p>
                </div>
              )}
              {meta.to_account && (
                <div>
                  <p className="text-gray-400 text-xs font-bold uppercase">To Account</p>
                  <p className="text-gray-900 font-semibold mt-0.5">{meta.to_account === 'cash' ? 'Cash' : meta.to_account}</p>
                </div>
              )}
              {meta.reference_no && (
                <div>
                  <p className="text-gray-400 text-xs font-bold uppercase">Reference</p>
                  <p className="text-gray-900 font-semibold mt-0.5">{meta.reference_no}</p>
                </div>
              )}
              {v.description && (
                <div className="col-span-2">
                  <p className="text-gray-400 text-xs font-bold uppercase">Narration</p>
                  <p className="text-gray-700 mt-0.5">{v.description}</p>
                </div>
              )}
            </div>
          </GlassCard>

          {/* Journal lines */}
          <GlassCard className="overflow-hidden" data-testid="voucher-journal-card">
            <div className="px-5 py-3 border-b border-gray-200 bg-gray-50">
              <p className="text-gray-500 text-xs font-bold uppercase tracking-wider">Journal Entry</p>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left px-5 py-2.5 text-gray-500 text-xs font-bold uppercase">Account</th>
                  <th className="text-left px-5 py-2.5 text-gray-500 text-xs font-bold uppercase">Type</th>
                  <th className="text-right px-5 py-2.5 text-gray-500 text-xs font-bold uppercase">Debit</th>
                  <th className="text-right px-5 py-2.5 text-gray-500 text-xs font-bold uppercase">Credit</th>
                </tr>
              </thead>
              <tbody>
                {(v.lines || []).map((l, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="px-5 py-2.5 text-gray-900 font-semibold">{l.account_name}</td>
                    <td className="px-5 py-2.5"><span className="px-2 py-0.5 rounded bg-gray-100 text-gray-500 text-[10px] font-bold uppercase">{l.account_type}</span></td>
                    <td className="px-5 py-2.5 text-right tabular-nums text-gray-700">{l.debit_rupees > 0 ? formatMoney(l.debit_rupees) : '—'}</td>
                    <td className="px-5 py-2.5 text-right tabular-nums text-gray-700">{l.credit_rupees > 0 ? formatMoney(l.credit_rupees) : '—'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-300 bg-gray-50">
                  <td colSpan={2} className="px-5 py-2.5 text-gray-900 font-extrabold text-xs uppercase">Totals</td>
                  <td className="px-5 py-2.5 text-right text-gray-900 font-extrabold tabular-nums">{formatMoney(v.debit_total)}</td>
                  <td className="px-5 py-2.5 text-right text-gray-900 font-extrabold tabular-nums">{formatMoney(v.credit_total)}</td>
                </tr>
              </tfoot>
            </table>
          </GlassCard>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Party balance */}
          {partyBalance != null && (
            <GlassCard className="p-5" data-testid="voucher-party-balance">
              <h3 className="text-gray-500 text-xs font-bold uppercase tracking-wider mb-2">Party Running Balance</h3>
              <p className="text-gray-900 font-extrabold text-xl tabular-nums">{formatMoney(partyBalance)}</p>
              {meta.party_name && <p className="text-gray-400 text-xs mt-1">{meta.party_name}</p>}
            </GlassCard>
          )}

          {/* Actions */}
          <GlassCard className="p-5">
            <h3 className="text-gray-500 text-xs font-bold uppercase tracking-wider mb-3">Actions</h3>
            <div className="space-y-2">
              {canReverse && isOld && (
                <button onClick={handleReverse} disabled={reversing} data-testid="voucher-reverse-btn"
                  className="w-full flex items-center justify-center gap-2 h-10 rounded-xl bg-red-50 text-red-600 text-sm font-bold border border-red-200 hover:bg-red-100 transition-colors disabled:opacity-50">
                  <RotateCcw size={14} /> {reversing ? 'Reversing...' : 'Reverse Entry'}
                </button>
              )}
              {canReverse && !isOld && (
                <p className="text-gray-400 text-xs text-center">Reversal available after 24h</p>
              )}
              {v.reversed_at && (
                <p className="text-gray-400 text-xs text-center">This voucher has been reversed</p>
              )}
            </div>
          </GlassCard>

          {/* Metadata */}
          <GlassCard className="p-5">
            <h3 className="text-gray-500 text-xs font-bold uppercase tracking-wider mb-3">Metadata</h3>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between"><span className="text-gray-400">Entry ID</span><span className="text-gray-700 font-mono">{v.entry_id?.slice(0, 12)}...</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Created</span><span className="text-gray-700">{fmtDate(v.created_at)}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Posted</span><span className={v.posted ? 'text-emerald-600 font-bold' : 'text-gray-400'}>{v.posted ? 'Yes' : 'No'}</span></div>
            </div>
          </GlassCard>
        </div>
      </div>
    </div>
  );
}
